import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { Field } from "./settings-form.tsx";

const SettingsSchema = z.object({
  delayLineAfterWorkingDays: z.number().int(),
  checkInAfterDays: z.number().int(),
  isDefault: z.boolean(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

/** The one route this section reads and writes: always a single workspace. */
export const lateReplySettingsPath = (workspaceId: string): string =>
  `/api/work/workspaces/${encodeURIComponent(workspaceId)}/late-reply-settings`;

/** What went wrong with a save, in the rep's terms. */
function saveFailure(status: number, body: unknown): string {
  if (status === 401) return "Your session ended. Sign in again.";
  const field = z.object({ field: z.string() }).safeParse(body);
  if (status === 422 && field.success && field.data.field === "checkInAfterDays")
    return "The check-in must come later than the apology: use more days than working days.";
  if (status === 422) return "Use whole numbers: 1 to 30 working days, and 2 to 90 days.";
  return "The thresholds were not saved.";
}

/**
 * The two thresholds a held reply's draft follows, on the workspace's Drafting page. A reply the app
 * held while its campaign was being set up is drafted with its age in mind: after the first, the
 * draft opens with one short apology for the slow reply; after the second, it is a short check-in
 * instead of a full answer.
 */
export function LateReplySettings({ workspaceId }: { readonly workspaceId: string }) {
  const cache = useQueryClient();
  const key = ["late-reply-settings", workspaceId];
  const settings = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await fetch(lateReplySettingsPath(workspaceId), {
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("The thresholds could not be read.");
      return SettingsSchema.parse(await response.json());
    },
  });
  const [draft, setDraft] = useState<{ delay: string; checkIn: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const data = settings.data;
  const shown =
    draft ??
    (data
      ? { delay: String(data.delayLineAfterWorkingDays), checkIn: String(data.checkInAfterDays) }
      : null);
  const save = async (): Promise<void> => {
    if (!shown) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(lateReplySettingsPath(workspaceId), {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          delayLineAfterWorkingDays: Number(shown.delay),
          checkInAfterDays: Number(shown.checkIn),
        }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        setMessage(saveFailure(response.status, body));
        return;
      }
      cache.setQueryData(key, SettingsSchema.parse(body));
      setDraft(null);
      setMessage("Saved. Replies drafted from now on follow these.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="lcr-late-replies" aria-labelledby="lcr-late-replies-title">
      <h2 id="lcr-late-replies-title">Replies that waited during setup</h2>
      <p className="lcr-settings-note">
        A reply that arrived while a campaign was being set up is drafted once the campaign goes
        live, however old it is. Its draft never confirms a time that has already passed.
      </p>
      {settings.isPending && <p className="lcr-settings-note">Loading…</p>}
      {settings.isError && (
        <p className="lcr-settings-error" role="alert">
          {settings.error.message}{" "}
          <button type="button" className="lcr-linklike" onClick={() => void settings.refetch()}>
            Try again
          </button>
        </p>
      )}
      {shown && (
        <form
          className="lcr-settings-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Field
            id="lcr-late-delay"
            label="Apologise for the delay after"
            hint="Working days since the lead wrote. The draft opens with “Sorry for the slow reply.”"
          >
            <span className="lcr-late-reply-input">
              <input
                id="lcr-late-delay"
                className="lcr-late-reply-number"
                type="number"
                inputMode="numeric"
                min={1}
                max={30}
                value={shown.delay}
                disabled={busy}
                onChange={(event) => setDraft({ ...shown, delay: event.target.value })}
              />
              <span>working days</span>
            </span>
          </Field>
          <Field
            id="lcr-late-check-in"
            label="Send a short check-in instead after"
            hint="Days since the lead wrote. The draft asks whether their question still stands instead of answering in full."
          >
            <span className="lcr-late-reply-input">
              <input
                id="lcr-late-check-in"
                className="lcr-late-reply-number"
                type="number"
                inputMode="numeric"
                min={2}
                max={90}
                value={shown.checkIn}
                disabled={busy}
                onChange={(event) => setDraft({ ...shown, checkIn: event.target.value })}
              />
              <span>days</span>
            </span>
          </Field>
          <div className="lcr-actions">
            <button type="submit" className="lcr-small-button" disabled={busy || draft === null}>
              {busy ? "Saving…" : "Save thresholds"}
            </button>
            {message ? (
              <span role="status">{message}</span>
            ) : (
              data?.isDefault &&
              draft === null && <span className="lcr-settings-hint">Using the defaults</span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
